import React from 'react';
import styled from 'styled-components';
import { colors } from '../styles/colors';
import Navigation from './Navigation';

const MainContent = styled.div`
  position: relative;
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const BackgroundImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover;
  background-color: ${colors.primary.darkTan};
  object-position: center center;

  @media (max-width: 768px) {
    object-position: left center; /* Show more of the left side on tablets */
  }

  @media (max-width: 480px) {
    object-position: center center;
  }

  @media (max-width: 600px) {
    object-position: 60% center; /* Show subjects but crop some left side */
    height: 100vh; /* Make taller */
    object-fit: cover;
  }

  @media (max-width: 375px) {
    object-position: 58% center; /* Shift right to pin right side */
  }

  @media (max-width: 350px) {
    object-position: 56% center; /* Shift more right for smaller screens */
  }
`;

const TextOverlay = styled.div`
  position: absolute;
  top: 40%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  z-index: 10;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0 12%;
  gap: 20px;

  @media (max-width: 768px) {
    top: 35%;
    padding: 0 6%;
    gap: 40px;
  }

  @media (max-width: 480px) {
    top: 30%;
    padding: 0 3%;
    gap: 60px;
  }

  @media (max-width: 600px) {
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    padding: 0 2% 0 55%;
    gap: 10px;
    top: 30%;
  }

  @media (max-width: 375px) {
    gap: 80px;
  }

  @media (max-width: 375px) and (max-width: 600px) {
    gap: 10px;
  }
`;

const Name = styled.div`
  font-family: 'GremeryTwo', cursive;
  font-size: clamp(2rem, 8vw, 4rem);
  color: ${colors.primary.gold};
  line-height: 0.8;
  letter-spacing: 2px;
  white-space: nowrap;

  @media (max-width: 1300px) {
    font-size: clamp(1.8rem, 6vw, 3.5rem);
  }

  @media (max-width: 1000px) {
    font-size: clamp(1.6rem, 5.5vw, 2.8rem);
  }

  @media (max-width: 840px) {
    font-size: clamp(1.4rem, 5vw, 2.5rem);
  }

  @media (max-width: 600px) {
    text-align: right;
    font-size: clamp(1.4rem, 5vw, 2.2rem); /* Increased font size */
    white-space: normal; /* Allow text wrapping if needed */
  }
`;

const DesktopNames = styled.div`
  display: contents;

  @media (max-width: 600px) {
    display: none;
  }
`;

const MobileNames = styled.div`
  display: none;

  @media (max-width: 600px) {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 10px;
  }
`;

const Ampersand = styled.div`
  font-family: 'GremeryTwo', cursive;
  font-size: clamp(2.8rem, 8vw, 4.4rem);
  color: ${colors.primary.gold};
  text-align: center;
  display: none;
  line-height: 1;

  @media (max-width: 600px) {
    display: block; /* Show ampersand to the right of stacked names */
  }
`;

const MarriageText = styled.div`
  position: absolute;
  top: 43%;
  right: 7%;
  font-family: 'saint-andrews-queen', cursive;
  font-size: clamp(2.4rem, 6vw, 4rem);
  color: ${colors.primary.gold};
  z-index: 10;

  @media (max-width: 1200px) {
    font-size: clamp(1.6rem, 4vw, 2.5rem);
  }
  
  @media (max-width: 768px) {
    top: 38%;
    right: 3%;
    font-size: clamp(1.6rem, 4vw, 2.5rem);
  }

  @media (max-width: 480px) {
    top: 33%;
    right: 2%;
    font-size: clamp(1.2rem, 3vw, 1.8rem);
  }

  @media (max-width: 600px) {
    top: 34%; 
    right: 6%; 
    text-align: right;
    font-size: clamp(2.0rem, 5.2vw, 2.7rem); /* Much bigger starting size at 600px */
  }

  @media (max-width: 500px) {
    font-size: clamp(1.8rem, 4.5vw, 2.6rem);
  }

  @media (max-width: 400px) {
    font-size: clamp(1.8rem, 4.5vw, 2.4rem);
  }

  @media (max-width: 375px) {
    font-size: clamp(1.8rem, 3.8vw, 2.0rem);
  }

  @media (max-width: 300px) {
    font-size: clamp(1.8rem, 3.8vw, 2.0rem); /* Smallest size at 300px */
  }

  @media (max-width: 375px) and (max-width: 600px) {
    font-size: clamp(1.6rem, 3.2vw, 2.0rem); /* Override for this size range */
  }
`;


const HeroSection: React.FC = () => {
  return (
    <MainContent>
      <BackgroundImage src="/images/lolla-kiss-full-bg.png" alt="Palm background" />
      <Navigation />
      <TextOverlay>
        <DesktopNames>
          <Name>GRANT</Name>
          <Name>EMERY</Name>
        </DesktopNames>
        <MobileNames>
          <Name>EMERY</Name>
          <Name>GRANT</Name>
        </MobileNames>
        <Ampersand>&</Ampersand>
      </TextOverlay>
      <MarriageText>are getting married</MarriageText>
    </MainContent>
  );
};

export default HeroSection; 